import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, LayoutGroup } from "framer-motion";
import { COLORS, STATUS_COLORS } from "../theme/colors";
import { useAgentContext } from "../contexts/AgentContext";
import { useAuth } from "../contexts/AuthContext";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { SUPERVISOR_ROLES } from "../utils/helpers";
import { IconButton } from "./ui";
import Icon from "./Icon";
import IconTile from "./IconTile";
import ChannelIcon from "./ChannelIcon";
import { navRouteFor, isNavItemActive } from "./navRouting";

const fmtVer = v => v ? `v${v.replace(/^v+/, "")}` : "";

const Nav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const bp = useBreakpoint();
  const { currentUser } = useAuth();
  const {
    agents, activeAgent, setActiveAgent,
    agentStatus, setAgentStatus, currentAgent,
    handleCloneAgent, handleAgentStatusChange,
  } = useAgentContext();

  // Agents is now the home page (replaces Builder which was the
  // legacy default). Per UX feedback: most users land here to find
  // existing agents, not to build new ones.
  // Nav-item active state + click routing now flow through the
  // navRouting helpers (isNavItemActive / navRouteFor) so items can carry
  // an explicit `route` + `matchPrefix` (B2 Observability). `setActiveTab`
  // stays for the agent/clone flyout buttons that navigate by bare id.
  const setActiveTab = (tab) => navigate(`/${tab}`);

  // M3.1 + M3.2 — expanded sidebar with category groupers.
  // Per §14.11 decision #22 (sign-off Q3): four categories.
  //   Build    = Agents (home), Builder, Marketplace
  //   Operate  = Channels, Campaigns, Schedules
  //   Observe  = Supervisor (role-gated), Analytics
  //   Settings = Admin
  const sections = [
    {
      label: "Build",
      items: [
        { id: "library",     iconName: "tab.agents",      category: "agents",      tip: "Agents" },
        { id: "builder",     iconName: "tab.builder",     category: "builder",     tip: "Builder" },
        { id: "marketplace", iconName: "tab.marketplace", category: "marketplace", tip: "Marketplace" },
        { id: "skills",      iconName: "tab.skills",      category: "skills",       tip: "Skills" },
      ],
    },
    {
      label: "Operate",
      items: [
        { id: "channels",  iconName: "tab.channels",  category: "channels",  tip: "Channels" },
        { id: "campaigns", iconName: "tab.campaigns", category: "campaigns", tip: "Campaigns" },
        { id: "schedules", iconName: "tab.schedules", category: "schedules", tip: "Schedules" },
      ],
    },
    {
      label: "Observe",
      items: [
        ...(SUPERVISOR_ROLES.includes(currentUser?.role)
          ? [{ id: "supervisor", iconName: "tab.supervisor", category: "supervisor", tip: "Supervisor" }]
          : []),
        { id: "analytics", iconName: "tab.analytics", category: "analytics", tip: "Analytics" },
        // B2 — promote the tenant Observability Hub (Datasets/Prompts/Evals/
        // Annotations) out of the Analytics ⚙ button into primary nav. It
        // routes to the hub's landing tab; `matchPrefix` keeps it highlighted
        // across every /analytics/observability/* sub-tab while leaving the
        // Analytics item (exact-path match) dark on those routes.
        { id: "observability", iconName: "tab.observability", category: "observability",
          tip: "Observability", route: "/analytics/observability/datasets",
          matchPrefix: "/analytics/observability" },
      ],
    },
    {
      label: "Settings",
      items: [
        { id: "admin", iconName: "tab.admin", category: "admin", tip: "Admin" },
      ],
    },
  ];
  // Flat list still used by the mobile bottom nav (no groupings on mobile).
  const items = sections.flatMap(s => s.items);

  // Sidebar collapsed state — persisted to localStorage so user preference
  // survives reloads (decision #21).
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("nav.sidebarCollapsed") === "1"; } catch { return false; }
  });
  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    try { localStorage.setItem("nav.sidebarCollapsed", next ? "1" : "0"); } catch {}
  };

  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [propsPanelOpen, setPropsPanelOpen] = useState(false);
  const [showCloneMenu, setShowCloneMenu] = useState(false);
  const [cloning, setCloning] = useState(null);

  if (bp.isMobile) {
    return (
      <div style={{
        position: "fixed", bottom: 0, left: 0, right: 0, height: 60, zIndex: 1000,
        background: COLORS.surface, borderTop: `1px solid ${COLORS.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-around",
        padding: "0 4px", backdropFilter: "blur(12px)",
      }}>
        {items.map(item => {
          const isActive = isNavItemActive(item, location.pathname);
          return (
          <button key={item.id} onClick={() => navigate(navRouteFor(item))} style={{
            flex: 1, height: 52, border: "none", cursor: "pointer",
            background: "transparent", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 3,
            color: isActive ? COLORS.accent : COLORS.textMuted,
            transition: "all 0.2s", position: "relative",
          }}>
            {isActive && <div style={{
              position: "absolute", top: -1, width: 24, height: 3, borderRadius: 2,
              background: COLORS.accent, boxShadow: `0 0 8px ${COLORS.accentGlow}`,
            }} />}
            <IconTile category={item.category} iconName={item.iconName}
              size={32} active={isActive} ariaLabel={item.tip} />
            <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: "0.02em" }}>{item.tip}</span>
          </button>
          );
        })}
        {/* Agent button in mobile nav */}
        <button onClick={() => setAgentPanelOpen(!agentPanelOpen)} style={{
          flex: 1, height: 52, border: "none", cursor: "pointer",
          background: "transparent", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", gap: 3,
          color: agentPanelOpen ? COLORS.accent : COLORS.textMuted,
          transition: "all 0.2s", position: "relative",
        }}>
          <div style={{ position: "relative" }}>
            <span style={{ fontSize: 20 }}>{"\u{1F916}"}</span>
            <div style={{
              position: "absolute", top: -1, right: -3, width: 7, height: 7, borderRadius: "50%",
              background: STATUS_COLORS[agentStatus], border: `2px solid ${COLORS.surface}`,
            }} />
          </div>
          <span style={{ fontSize: 9, fontWeight: 600 }}>Agent</span>
        </button>

        {/* Mobile agent panel */}
        {agentPanelOpen && (
          <div onClick={() => setAgentPanelOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(0,0,0,0.4)" }}>
            <div onClick={e => e.stopPropagation()} className="panel-scroll" style={{
              position: "absolute", bottom: 64, left: 8, right: 8, maxHeight: "70vh",
              background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16,
              boxShadow: "0 -8px 40px rgba(0,0,0,0.5)", animation: "slideUp 0.2s ease-out",
              overflowY: "auto", padding: 8,
            }}>
              <div style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Switch Agent
              </div>
              {agents.length === 0 ? (
                <div style={{ padding: "20px 12px", textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>No agents yet</div>
              ) : agents.map(agent => (
                <button key={agent.id} onClick={() => { setActiveAgent(agent.id); setAgentStatus(agent.status); setAgentPanelOpen(false); }} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 10,
                  padding: "12px 10px", borderRadius: 10, border: "none", cursor: "pointer",
                  background: activeAgent === agent.id ? COLORS.surfaceHover : "transparent", textAlign: "left",
                }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_COLORS[agent.status], flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{agent.name} <span style={{ fontSize: 10, color: COLORS.textDim }}>{fmtVer(agent.version) || "v1.0"}</span></div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>{agent.model} {"\u00B7"} {(agent.channels || []).join(", ") || "No channels"}</div>
                  </div>
                  {activeAgent === agent.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.accent }} />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Desktop sidebar — M3.1 + M3.2: expanded 224px with icon + label +
  // category groupers; 64px icon-only when collapsed.
  const sidebarWidth = sidebarCollapsed ? 64 : 224;

  return (
    <div style={{
      width: sidebarWidth,
      background: COLORS.surface,
      borderRight: `1px solid ${COLORS.border}`,
      display: "flex", flexDirection: "column",
      paddingTop: 12, paddingBottom: 12,
      position: "relative", zIndex: 10, flexShrink: 0,
      transition: "width 0.22s ease",
    }}>
      {/* Collapse toggle row. With the "A" badge removed, the only
          child left is the toggle button — right-align it so it tucks
          into the corner instead of floating mid-row from the previous
          space-between layout. */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: sidebarCollapsed ? "center" : "flex-end",
        padding: sidebarCollapsed ? "0" : "0 12px",
        marginBottom: 16,
        gap: 8,
      }}>
        {/* Removed: orphan "A" badge gradient div. The brand lives in
            the Shell topbar (Brand slot) — having a second mark in the
            sidebar header was meaningless and crowded the toggle. */}
        {!sidebarCollapsed && (
          <button onClick={toggleSidebar} title="Collapse sidebar" style={{
            width: 28, height: 28, borderRadius: 6, border: "none",
            background: "transparent", color: COLORS.textMuted, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.15s",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = COLORS.surfaceHover; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
          >
            <Icon name="chevrons.left" size={14} color={COLORS.textMuted} />
          </button>
        )}
      </div>
      {sidebarCollapsed && (
        <button onClick={toggleSidebar} title="Expand sidebar" style={{
          width: 28, height: 28, borderRadius: 6, border: "none",
          background: "transparent", color: COLORS.textMuted, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 12px",
        }}>
          <Icon name="chevrons.right" size={14} color={COLORS.textMuted} />
        </button>
      )}

      {/* Sections — wrapped in LayoutGroup so the active-tab indicator
          (motion.div with layoutId="i2-nav-active") slides smoothly
          between sibling buttons on route change (M3.3 / decision #26). */}
      <LayoutGroup id="i2-nav-active-group">
      <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: sidebarCollapsed ? "0" : "0 8px" }}>
        {sections.map((section, sIdx) => (
          <React.Fragment key={section.label}>
            {!sidebarCollapsed && (
              <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                textTransform: "uppercase", color: COLORS.textDim,
                padding: sIdx === 0 ? "0 12px 6px" : "12px 12px 6px",
              }}>
                {section.label}
              </div>
            )}
            {section.items.map(item => {
              const isActive = isNavItemActive(item, location.pathname);
              return (
                <button
                  key={item.id}
                  onClick={() => { navigate(navRouteFor(item)); setAgentPanelOpen(false); setPropsPanelOpen(false); }}
                  title={sidebarCollapsed ? item.tip : undefined}
                  style={{
                    width: "100%",
                    height: 44,
                    border: "none",
                    cursor: "pointer",
                    background: "transparent",
                    color: isActive ? COLORS.accent : COLORS.text,
                    display: "flex", alignItems: "center",
                    justifyContent: sidebarCollapsed ? "center" : "flex-start",
                    gap: 12,
                    padding: sidebarCollapsed ? "0" : "0 10px",
                    borderRadius: 10,
                    transition: "color 0.15s",
                    position: "relative",
                    isolation: "isolate",
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = COLORS.surfaceHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                >
                  {/* Sliding pill — single motion.div per Nav, layoutId
                      hops between active buttons on route change. */}
                  {isActive && (
                    <motion.div
                      layoutId="i2-nav-active"
                      transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.7 }}
                      style={{
                        position: "absolute",
                        inset: 0,
                        background: COLORS.accentSoft,
                        borderRadius: 10,
                        zIndex: -1,
                      }}
                    />
                  )}
                  {/* Sliding edge bar — accent rail outside the button left edge */}
                  {isActive && (
                    <motion.div
                      layoutId="i2-nav-active-bar"
                      transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.7 }}
                      style={{
                        position: "absolute",
                        left: sidebarCollapsed ? -8 : -2,
                        top: "50%", transform: "translateY(-50%)",
                        width: 3, height: 22, borderRadius: 2,
                        background: COLORS.accent,
                        boxShadow: `0 0 8px ${COLORS.accentGlow}`,
                      }}
                    />
                  )}
                  <IconTile
                    category={item.category}
                    iconName={item.iconName}
                    size={32}
                    active={isActive}
                    ariaLabel={item.tip}
                  />
                  {!sidebarCollapsed && (
                    <span style={{
                      fontSize: 13,
                      fontWeight: isActive ? 600 : 500,
                      letterSpacing: "0.01em",
                      whiteSpace: "nowrap",
                    }}>
                      {item.tip}
                    </span>
                  )}
                </button>
              );
            })}
          </React.Fragment>
        ))}
      </div>
      </LayoutGroup>

      <div style={{ flex: 1 }} />

      {/* Divider */}
      <div style={{
        width: sidebarCollapsed ? 28 : "calc(100% - 24px)",
        height: 1, background: COLORS.border,
        margin: sidebarCollapsed ? "4px auto" : "8px 12px",
      }} />

      {/* Agent Status Button */}
      <button onClick={() => { setAgentPanelOpen(!agentPanelOpen); setPropsPanelOpen(false); }} title="Switch Agent" style={{
        width: 44, height: 44, borderRadius: 12, border: "none", cursor: "pointer",
        background: agentPanelOpen ? COLORS.accentSoft : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s", position: "relative",
      }}>
        {agentPanelOpen && <div style={{
          position: "absolute", left: -10, width: 3, height: 20, borderRadius: 2,
          background: COLORS.accent, boxShadow: `0 0 8px ${COLORS.accentGlow}`
        }} />}
        <div style={{ position: "relative" }}>
          <span style={{ fontSize: 18 }}>{"\u{1F916}"}</span>
          <div style={{
            position: "absolute", bottom: -2, right: -4, width: 8, height: 8, borderRadius: "50%",
            background: STATUS_COLORS[agentStatus], border: `2px solid ${COLORS.surface}`,
            boxShadow: `0 0 4px ${STATUS_COLORS[agentStatus]}66`,
          }} />
        </div>
      </button>

      {/* Properties Button */}
      <button onClick={() => { setPropsPanelOpen(!propsPanelOpen); setAgentPanelOpen(false); }} title="Agent Properties" style={{
        width: 44, height: 44, borderRadius: 12, border: "none", cursor: "pointer",
        background: propsPanelOpen ? COLORS.accentSoft : "transparent",
        color: propsPanelOpen ? COLORS.accent : COLORS.textMuted,
        fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.2s", position: "relative", marginBottom: 4,
      }}>
        {propsPanelOpen && <div style={{
          position: "absolute", left: -10, width: 3, height: 20, borderRadius: 2,
          background: COLORS.accent, boxShadow: `0 0 8px ${COLORS.accentGlow}`
        }} />}
        {"\u25C9"}
      </button>


      {/* Agent Selector Flyout */}
      {agentPanelOpen && (
        <div onClick={() => setAgentPanelOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 500 }}>
          <div onClick={e => e.stopPropagation()} className="panel-scroll" style={{
            position: "absolute", left: 72, bottom: 100, width: 360, maxHeight: "60vh",
            background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14,
            boxShadow: `0 12px 48px rgba(0,0,0,0.5), 0 0 0 1px ${COLORS.border}`,
            animation: "fadeIn 0.12s ease-out", padding: 8, overflowY: "auto",
          }}>
            <div style={{ padding: "8px 12px", fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Switch Agent
            </div>
            {agents.length === 0 ? (
              <div style={{ padding: "20px 12px", textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>No agents yet. Create one in the Builder.</div>
            ) : agents.map(agent => (
              <button key={agent.id} onClick={() => { setActiveAgent(agent.id); setAgentStatus(agent.status); setAgentPanelOpen(false); }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12,
                padding: "12px 12px", borderRadius: 10, border: "none", cursor: "pointer",
                background: activeAgent === agent.id ? COLORS.surfaceHover : "transparent",
                transition: "all 0.12s", textAlign: "left",
              }}
                onMouseEnter={e => { if (activeAgent !== agent.id) e.currentTarget.style.background = COLORS.surfaceHover; }}
                onMouseLeave={e => { if (activeAgent !== agent.id) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: `${STATUS_COLORS[agent.status]}10`, border: `1px solid ${STATUS_COLORS[agent.status]}25`,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0,
                }}>{"\u{1F916}"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{agent.name}</span>
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>{fmtVer(agent.version) || "v1.0"}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      <div style={{ width: 6, height: 6, borderRadius: "50%", background: STATUS_COLORS[agent.status], boxShadow: `0 0 4px ${STATUS_COLORS[agent.status]}66` }} />
                      <span style={{ fontSize: 10, color: STATUS_COLORS[agent.status], fontWeight: 600, textTransform: "capitalize" }}>{agent.status}</span>
                    </div>
                    <span style={{ fontSize: 10, color: COLORS.textDim }}>{"\u00B7"}</span>
                    <span style={{ fontSize: 10, color: COLORS.textMuted }}>{agent.model}</span>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 3, flexShrink: 0 }}>
                  {(agent.channels || []).map(c => <ChannelIcon key={c} channel={c} size={12} />)}
                </div>
                {activeAgent === agent.id && <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.accent, flexShrink: 0 }} />}
              </button>
            ))}
            <div style={{ padding: "4px 0", borderTop: `1px solid ${COLORS.border}`, marginTop: 4 }}>
              <button onClick={() => setShowCloneMenu(!showCloneMenu)} style={{
                width: "100%", padding: "10px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                background: showCloneMenu ? COLORS.surfaceHover : "transparent",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                color: COLORS.accentText, fontSize: 12, fontWeight: 600,
              }}
                onMouseEnter={e => { if (!showCloneMenu) e.currentTarget.style.background = COLORS.surfaceHover; }}
                onMouseLeave={e => { if (!showCloneMenu) e.currentTarget.style.background = "transparent"; }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={{ fontSize: 14 }}>+</span> Create New Agent</span>
                <span style={{ fontSize: 10, color: COLORS.textDim, transform: showCloneMenu ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>{"\u25BE"}</span>
              </button>
              {showCloneMenu && (
                <div style={{ padding: "4px 6px 6px" }}>
                  {/* Blank agent */}
                  <button onClick={() => { setActiveTab("builder"); setAgentPanelOpen(false); setShowCloneMenu(false); }} style={{
                    width: "100%", display: "flex", alignItems: "center", gap: 10,
                    padding: "9px 10px", borderRadius: 8, border: `1px dashed ${COLORS.border}`,
                    background: "transparent", cursor: "pointer", textAlign: "left", marginBottom: 4,
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = COLORS.surfaceHover; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: 7, border: `1px dashed ${COLORS.accent}40`,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, color: COLORS.accentText, flexShrink: 0,
                    }}>+</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>Blank Agent</div>
                      <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 1 }}>Start from scratch in Builder</div>
                    </div>
                  </button>
                  {/* Clone from existing */}
                  {agents.length > 0 && (
                    <>
                      <div style={{ fontSize: 9, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.08em", textTransform: "uppercase", padding: "6px 10px 4px" }}>
                        Clone From Existing
                      </div>
                      {agents.map(agent => (
                        <button key={agent.id} disabled={cloning === agent.id} onClick={async () => {
                          setCloning(agent.id);
                          const res = await handleCloneAgent(agent);
                          setCloning(null);
                          if (res?.success) { setAgentPanelOpen(false); setShowCloneMenu(false); }
                        }} style={{
                          width: "100%", display: "flex", alignItems: "center", gap: 10,
                          padding: "8px 10px", borderRadius: 8, border: "none",
                          background: "transparent", cursor: cloning === agent.id ? "wait" : "pointer", textAlign: "left",
                          opacity: cloning && cloning !== agent.id ? 0.4 : 1,
                        }}
                          onMouseEnter={e => { e.currentTarget.style.background = COLORS.surfaceHover; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                        >
                          <div style={{
                            width: 28, height: 28, borderRadius: 7,
                            background: `${STATUS_COLORS[agent.status]}10`, border: `1px solid ${STATUS_COLORS[agent.status]}25`,
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0,
                          }}>{"\u{1F916}"}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {cloning === agent.id ? "Cloning..." : agent.name}
                            </div>
                            <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 1 }}>{agent.model} {"\u00B7"} {fmtVer(agent.version) || "v1.0"}</div>
                          </div>
                          <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                            {(agent.channels || []).map(c => <ChannelIcon key={c} channel={c} size={10} />)}
                          </div>
                        </button>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Properties Flyout */}
      {propsPanelOpen && (
        <div onClick={() => setPropsPanelOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 500 }}>
          <div onClick={e => e.stopPropagation()} className="panel-scroll" style={{
            position: "absolute", left: 72, bottom: 60, width: 340,
            background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14,
            boxShadow: `0 12px 48px rgba(0,0,0,0.5), 0 0 0 1px ${COLORS.border}`,
            animation: "fadeIn 0.12s ease-out", overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{
              padding: "16px 18px", borderBottom: `1px solid ${COLORS.border}`,
              backgroundImage: `radial-gradient(ellipse at top right, ${STATUS_COLORS[currentAgent.status]}08, transparent 60%)`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 18 }}>{"\u{1F916}"}</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{currentAgent.name}</div>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 1 }}>{fmtVer(currentAgent.version) || "v1.0"}{currentAgent.created_at ? ` \u00B7 ${new Date(currentAgent.created_at).toLocaleDateString()}` : ""}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 4 }}>
                {["live", "paused", "draft"].map(s => (
                  <button key={s} onClick={() => handleAgentStatusChange(s)} style={{
                    padding: "5px 12px", borderRadius: 7, fontSize: 10, fontWeight: 600, cursor: "pointer",
                    background: agentStatus === s ? `${STATUS_COLORS[s]}15` : "transparent",
                    border: `1px solid ${agentStatus === s ? STATUS_COLORS[s] + "40" : COLORS.border}`,
                    color: agentStatus === s ? STATUS_COLORS[s] : COLORS.textMuted,
                    textTransform: "capitalize", transition: "all 0.15s",
                  }}>{s === "live" && "\u25CF "}{s}</button>
                ))}
              </div>
            </div>

            {/* Config grid */}
            <div style={{ padding: "14px 18px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 10 }}>Configuration</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  { label: "Model", value: currentAgent.model || "\u2014", icon: "\u{1F9E0}", color: COLORS.accentText },
                  { label: "Temperature", value: (currentAgent.temperature ?? 0).toString(), icon: "\u{1F321}", color: COLORS.orange },
                  { label: "Max Tokens", value: (currentAgent.max_tokens || 0).toLocaleString(), icon: "\u26A1", color: COLORS.green },
                  { label: "Status", value: currentAgent.status || "draft", icon: "\u25CE", color: COLORS.cyan },
                ].map(prop => (
                  <div key={prop.label} style={{ padding: "8px 10px", borderRadius: 8, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
                      <span style={{ fontSize: 11 }}>{prop.icon}</span>
                      <span style={{ fontSize: 9, color: COLORS.textMuted, fontWeight: 500 }}>{prop.label}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: prop.color }}>{prop.value}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Channels */}
            <div style={{ padding: "0 18px 14px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>Channels</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["whatsapp", "chat", "voice", "sms", "email"].map(ch => {
                  const active = (currentAgent.channels || []).includes(ch);
                  return (
                    <div key={ch} style={{
                      flex: 1, padding: "8px 0", borderRadius: 8, textAlign: "center", cursor: "pointer",
                      background: active ? `${COLORS.green}10` : COLORS.bg,
                      border: `1px solid ${active ? COLORS.green + "30" : COLORS.border}`,
                    }}>
                      <div><ChannelIcon channel={ch} size={16} /></div>
                      <div style={{ fontSize: 9, color: active ? COLORS.green : COLORS.textDim, marginTop: 2, fontWeight: 500, textTransform: "capitalize" }}>{ch}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quick actions */}
            <div style={{ padding: "0 8px 8px", display: "flex", gap: 4 }}>
              {[
                { label: "Edit Flow", icon: "\u2B21", action: () => { setActiveTab("builder"); setPropsPanelOpen(false); } },
                { label: "Analytics", icon: "\u25E9", action: () => { setActiveTab("analytics"); setPropsPanelOpen(false); } },
                // B51 (2026-07-23): was a NO-OP (`action: () => {}`) \u2014
                // the button rendered but did nothing, reported by users
                // as "no clone available." handleCloneAgent was already
                // destructured above, just never called.
                { label: "Duplicate", icon: "\u25EB", action: async () => {
                  const r = await handleCloneAgent(currentAgent);
                  if (r?.success) {
                    setPropsPanelOpen(false);
                    navigate("/library");
                  }
                } },
              ].map(act => (
                <button key={act.label} onClick={act.action} style={{
                  flex: 1, padding: "8px 0", borderRadius: 7, border: `1px solid ${COLORS.border}`,
                  background: "transparent", color: COLORS.textMuted, fontSize: 10, fontWeight: 500,
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                  transition: "all 0.12s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = COLORS.surfaceHover; e.currentTarget.style.color = COLORS.text; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = COLORS.textMuted; }}
                >
                  <span>{act.icon}</span> {act.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Nav;
